import React from "react";
import FileUpload from "./FileUpload";
import QueryPopup from "./QueryPopup";
import MessageRating from "./MessageRating";
import { BadgeInfo, Loader2 } from "lucide-react";

const ChatMessage = ({
  msg,
  index,
  userIsAdmin,
  queryPopupIndex,
  toggleQueryPopup,
  popupRef,
  ratedMessages,
  handleRateMessage,
  isBotTyping,
  isExpanded,
}) => {
  const renderFormattedQuery = (query) => {
    if (!query) return <p className="text-sm text-gray-500">No query information available</p>;

    return (
      <div
        className="self-start animate-slideInLeft"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          margin: '4px 0',
          borderRadius: '12px',
          backgroundColor: '#EFF6FF',
          border: '1px solid #BFDBFE',
          color: '#3B82F6',
          fontSize: '12px',
          fontWeight: 500,
          maxWidth: '80%',
        }}
      >
        {msg.isLoading && <Loader2 size={13} className="animate-spin" style={{ flexShrink: 0 }} />}
        <span>{msg.text}</span>
      </div>
    );
  };
  if (msg.isStreaming) return null;
  
  const isUser = msg.sender === 'user';

  const renderMessageContent = () => {
    if (msg.type === "file") {
      return <FileUpload msg={msg} />;
    } else if (msg.type === "iframe") {
      return (
        <div
          className="w-full my-2 overflow-hidden bg-white"
          style={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
        >
          <iframe
            src={msg.url}
            title="Embedded Content"
            className="w-full h-[300px]"
            frameBorder="0"
          />
        </div>
      );
    } else if (msg.type === "iframe-html") {
      return (
        <div
          className="w-full my-2 overflow-hidden bg-white"
          style={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
        >
          <iframe
            srcDoc={msg.html}
            title="Portfolio Chart"
            sandbox="allow-scripts"
            style={{ width: '100%', height: isExpanded ? '55vh' : '280px', border: 'none' }}
          />
        </div>
      );
    } else {
      return (
        <div
          className="break-words relative"
          style={{ fontSize: '14px', lineHeight: '1.5' }}
        >
          <span dangerouslySetInnerHTML={{ __html: msg.text }} />
          {!isUser && (
            <>
              <MessageRating
                messageIndex={index}
                ratedMessages={ratedMessages}
                handleRateMessage={handleRateMessage}
              />
              <BadgeInfo
                size={17}
                aria-label="View audit info"
                className="absolute -bottom-2 -right-2 cursor-pointer bg-white rounded-full p-0.5 border border-gray-200 transition-all duration-200 hover:scale-110"
                style={{ color: '#6B7280', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--color-primary)'}
                onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}
                onClick={() => toggleQueryPopup(index)}
              />
            </>
          )}
        </div>
      );
    }
  };

  const isChart = msg.type === 'iframe-html' || msg.type === 'iframe';

  return (
    <div
      className={`relative group ${isUser ? 'self-end animate-slideInRight' : 'self-start animate-slideInLeft'}`}
      style={{
        maxWidth: isChart ? '100%' : '80%',
        width: isChart ? '100%' : undefined,
        padding: isChart ? '0' : '12px 16px',
        margin: '8px 0',
        borderRadius: '20px',
        ...(isChart
          ? {}
          : isUser
          ? {
              background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
              color: 'white',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              borderBottomRightRadius: '4px',
            }
          : {
              backgroundColor: '#F3F4F6',
              color: '#1F2937',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              borderBottomLeftRadius: '4px',
            }
        ),
      }}
      aria-live={!isUser ? 'polite' : undefined}
    >
      {renderMessageContent()}
    </div>
  );
};

export default ChatMessage;
